import { t, useLocale } from "@/i18n/locale";
import { Message } from "@/i18n/Message";
import {
  Component,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ArrowLeft, ChevronRight, AlertCircle, Loader2 } from "lucide-react";
import "./settings.css";

export function SettingsPage({
  title,
  description,
  onBack,
  backLabel = "Settings",
  children,
}: {
  title: string;
  description?: ReactNode;
  onBack?: () => void;
  backLabel?: string;
  children: ReactNode;
}) {
  useLocale();
  return (
    <section className="settings-page">
      {onBack && (
        <button className="settings-back" onClick={onBack}>
          <ArrowLeft size={16} aria-hidden />
          {t(backLabel)}
        </button>
      )}
      <header className="settings-page-heading">
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </header>
      {children}
    </section>
  );
}
export function SettingsSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="settings-section">
      <header>
        <h2>{title}</h2>
        {description && <p className="settings-description">{description}</p>}
      </header>
      {children}
    </section>
  );
}
export function SettingNavigationRow({
  title,
  description,
  value,
  onClick,
  icon,
}: {
  title: string;
  description?: ReactNode;
  value?: ReactNode;
  onClick: () => void;
  icon?: ReactNode;
}) {
  return (
    <button
      type="button"
      className="setting-navigation-row settings-index-row"
      onClick={onClick}
    >
      {icon && (
        <span className="setting-navigation-icon" aria-hidden>
          {icon}
        </span>
      )}
      <span className="setting-navigation-label">
        <span className="settings-index-row-title">{title}</span>
        {description && (
          <span className="settings-index-row-desc">{description}</span>
        )}
      </span>
      {value && <span className="setting-navigation-value">{value}</span>}
      <ChevronRight size={17} aria-hidden />
    </button>
  );
}
export function SettingField({
  label,
  htmlFor,
  description,
  children,
}: {
  label: string;
  htmlFor?: string;
  description?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="setting-field">
      {htmlFor ? (
        <label htmlFor={htmlFor}>{label}</label>
      ) : (
        <span className="setting-field-label">{label}</span>
      )}
      {children}
      {description && <p className="settings-description">{description}</p>}
    </div>
  );
}
export function SettingStatusRow({
  label,
  children,
}: {
  label: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="setting-status-row">
      <span>{label}</span>
      <div>{children}</div>
    </div>
  );
}
export type AsyncValue<T> =
  { status: "loading" } | { status: "error" } | { status: "ready"; data: T };
export function useSettingsResource<T>(
  load: () => Promise<T>,
  enabled = true,
  keepPreviousWhileRefreshing = false,
) {
  const [state, setState] = useState<AsyncValue<T>>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);
  const loader = useRef(load);
  loader.current = load;
  const retry = useCallback(() => setAttempt((v) => v + 1), []);
  useEffect(() => {
    if (!enabled) return;
    let active = true;
    setState((previous) =>
      keepPreviousWhileRefreshing && previous.status === "ready"
        ? previous
        : { status: "loading" },
    );
    void Promise.resolve()
      .then(() => loader.current())
      .then((data) => {
        if (active) setState({ status: "ready", data });
      })
      .catch(() => {
        if (active) setState({ status: "error" });
      });
    return () => {
      active = false;
    };
  }, [enabled, attempt, keepPreviousWhileRefreshing]);
  return { state, retry };
}
export function AsyncSection<T>({
  state,
  label,
  retry,
  children,
}: {
  state: AsyncValue<T>;
  label: string;
  retry: () => void;
  children: (data: T) => ReactNode;
}) {
  if (state.status === "loading")
    return (
      <div className="settings-loading" role="status">
        <Loader2 size={17} aria-hidden />
        <Message message={"Loading {{value0}}…"} values={{ value0: label }} />
      </div>
    );
  if (state.status === "error")
    return (
      <div className="settings-error" role="alert">
        <AlertCircle size={18} aria-hidden />
        <div>
          <Message message={"Couldn’t load {{value0}}."} values={{ value0: label }} /><br />
          <button className="settings-link" onClick={retry}><Message message={"Try again"} /></button>
        </div>
      </div>
    );
  return <>{children(state.data)}</>;
}
export function SaveBar({
  children,
  busy,
  onCancel,
  onSave,
  disabled,
  error,
  label = "Save changes",
}: {
  children: ReactNode;
  busy?: boolean;
  onCancel: () => void;
  onSave: () => void;
  disabled?: boolean;
  error?: ReactNode;
  label?: string;
}) {
  useLocale();
  return (
    <div className="settings-save-bar">
      <div className="settings-save-summary">
        <div role="status">{children}</div>
        {error && (
          <div className="settings-save-error" role="alert">
            {typeof error === "string" ? t(error) : error}
          </div>
        )}
      </div>
      <div className="settings-save-actions">
        <button
          type="button"
          className="settings-button"
          disabled={busy}
          onClick={onCancel}
        ><Message message={"Cancel"} /></button>
        <button
          type="button"
          className="settings-button settings-button--primary"
          disabled={busy || disabled}
          onClick={onSave}
        >
          {busy ? t("Saving…") : t(label)}
        </button>
      </div>
    </div>
  );
}
export function ConfirmDialog({
  title,
  children,
  onCancel,
  onConfirm,
  confirmLabel = "Disconnect",
  cancelLabel,
  tone = "danger",
  busy = false,
}: {
  title: string;
  children: ReactNode;
  onCancel: () => void;
  onConfirm: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: "primary" | "danger";
  busy?: boolean;
}) {
  useLocale();
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const el = ref.current;
    el?.showModal?.();
    return () => el?.close?.();
  }, []);
  return (
    <dialog
      ref={ref}
      className="settings-dialog"
      aria-label={title}
      onCancel={(e) => {
        e.preventDefault();
        if (!busy) onCancel();
      }}
    >
      <h2>{title}</h2>
      <div className="settings-dialog-body">{children}</div>
      <div className="settings-dialog-actions">
        <button
          type="button"
          className="settings-button"
          autoFocus
          disabled={busy}
          onClick={onCancel}
        >
          {t(cancelLabel ??
            (confirmLabel === "Discard" ? "Keep editing" : "Cancel"))}
        </button>
        <button
          type="button"
          className={`settings-button settings-button--${tone}`}
          disabled={busy}
          onClick={onConfirm}
        >
          {busy ? t("Working…") : t(confirmLabel)}
        </button>
      </div>
    </dialog>
  );
}
export class SettingsErrorBoundary extends Component<
  { children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    return this.state.failed ? (
      <div className="settings-error" role="alert">
        <Message message={"This settings page couldn’t be displayed."} />{" "}
        <button
          className="settings-button"
          onClick={() => window.location.reload()}
        ><Message message={"Reload page"} /></button>
      </div>
    ) : (
      this.props.children
    );
  }
}
